import { FC, Dispatch, useState } from 'react';

// MUI
import Box from '@mui/material/Box';

// screen
import { TemplateSettings } from 'modules/map/components/daily-dish/types';
import StyledAccordion from 'modules/map/components/daily-dish/components/utils/StyledAccordion';
import { SettingsAction } from 'modules/map/components/daily-dish/reducer';
import { Clean, Click, Heart, HeartFilled, HeartOutlined } from 'svg';
import UtilsIcon from 'modules/map/components/daily-dish/components/utils/UtilsIcon';
import TemplateLayoutSelector from 'modules/map/components/daily-dish/components/template-settings-panel/TemplateLayoutSelector';
import ToggleLogos from 'modules/map/components/daily-dish/components/template-settings-panel/ToggleLogos';
import FontStylesSelector from 'modules/map/components/daily-dish/components/template-settings-panel/FontStylesSelector';
import BackgroundSelector from 'modules/map/components/daily-dish/components/template-settings-panel/BackgroundSelector';
import DailyMenuTemplateSettingsPanel from 'modules/map/components/daily-dish/components/template-settings-panel/DailyMenuTemplateSettingsPanel';

const CyclePresetIcon = ({
  width = '24px',
  height = '24px',
}: {
  width?: string;
  height?: string;
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.5 12A8.5 8.5 0 0 1 18 5.5M21 4v6h-6"
      stroke="#0F0F10"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M20.5 12A8.5 8.5 0 0 1 6 18.5M3 20v-6h6"
      stroke="#0F0F10"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface TemplateSettingsPanelProps {
  settings: TemplateSettings;
  dispatch: Dispatch<SettingsAction>;
  show: boolean;
  lockedTheme: string | null;
  previewImages: string[];
  schoolYear?: number | null;
  onLockTheme: (themeId: string | null) => void;
  onResetWeeklyLook: () => void;
  onCyclePreset: () => void;
  onUploadBackground: (file: File) => void;
}

const TemplateSettingsPanel: FC<TemplateSettingsPanelProps> = (props) => {
  const {
    settings,
    dispatch,
    show,
    lockedTheme,
    previewImages,
    schoolYear,
    onLockTheme,
    onResetWeeklyLook,
    onCyclePreset,
    onUploadBackground,
  } = props;

  const [isThisSettingInFavoritesList, setIsThisSettingInFavoritesList] = useState<boolean>(false);

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        width: show
          ? {
              xs: '600px',
              sm: '700px',
              md: '850px',
              lg: '1000px',
            }
          : '0px',
        bgcolor: 'background.default',
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        borderLeft: show ? 1 : 0,
        borderColor: 'divider',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          p: 2,
          height: '100%',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          width: {
            xs: '600px',
            sm: '700px',
            md: '850px',
            lg: '1000px',
          },
        }}
      >
        <Box
          sx={{
            my: 1,
            display: 'flex',
            gap: 1,
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}
        >
          <UtilsIcon
            label="Yêu thích"
            icon={isThisSettingInFavoritesList ? <HeartFilled /> : <Heart />}
            hoverIcon={isThisSettingInFavoritesList ? <HeartFilled /> : <HeartOutlined />}
            hoverStrokeColor="#0A78A8"
            onClick={() => setIsThisSettingInFavoritesList((prev) => !prev)}
          />
          <UtilsIcon
            label="Cấu hình yêu thích"
            icon={<Click />}
            onClick={() => {
              console.log('click config');
            }}
          />
          <UtilsIcon label="Xoay preset" icon={<CyclePresetIcon />} onClick={onCyclePreset} />
          <UtilsIcon label="Reset về combo tuần" icon={<Clean />} onClick={onResetWeeklyLook} />
        </Box>

        <StyledAccordion label="Chung" isDefaultExpanded={false}>
          <TemplateLayoutSelector settings={settings} dispatch={dispatch} />
          <ToggleLogos settings={settings} dispatch={dispatch} />
          <BackgroundSelector
            settings={settings}
            dispatch={dispatch}
            lockedTheme={lockedTheme}
            previewImages={previewImages}
            schoolYear={schoolYear}
            onLockTheme={onLockTheme}
            onUpload={onUploadBackground}
          />
        </StyledAccordion>

        <StyledAccordion label="Chữ" isDefaultExpanded={false}>
          <FontStylesSelector settings={settings} dispatch={dispatch} />
        </StyledAccordion>

        <StyledAccordion label="Thực đơn ngày" isDefaultExpanded={true}>
          <DailyMenuTemplateSettingsPanel settings={settings} dispatch={dispatch} />
        </StyledAccordion>
      </Box>
    </Box>
  );
};

export default TemplateSettingsPanel;
